import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DsButton, DsInput } from '@/modules/internal-v2/ds';
import { ContactEmployeePicker } from '@/modules/internal-v2/shared/contact-employee-picker.component';
import { IfPermitted } from '@/modules/internal-v2/shared/if-permitted.component';
import { PERMISSIONS } from '@/constants/permissions';
import { useContactLists } from '@/api/contact-list/contact-list.hooks';
import { useGetAllContactEmployees } from '@/api/business-partner/business-partner.hooks';
import {
  useEmailBuilderSends,
  useRecipientConsent,
  useSendEmailDraft,
  useTestSendEmailDraft,
} from '@/api/email-builder/email-builder.hooks';
import type {
  EmailBuilderSendSummary,
  RenderEmailRequest,
  SendEmailRequest,
} from '@/api/email-builder/email-builder.types';
import type { ContactEmployee } from '@/api/contact-employee/contact-employee.types';

interface RecipientsPaneProps {
  /** Live draft from the page (subject, blocks, contactPerson, …). */
  draft: RenderEmailRequest;
  /** Picked Verteiler ids (multi-select). Lifted to the page so it survives tab switches. */
  contactListIds: number[];
  onContactListIdsChange: (next: number[]) => void;
  /** Ad-hoc contact-employee ids (multi-select). Same lifting reason. */
  adHocContactIds: number[];
  onAdHocContactIdsChange: (next: number[]) => void;
  /** Caller-controlled subject mirror (synced with the editor's subject field). */
  subject: string;
  onSubjectChange: (value: string) => void;
}

/**
 * Recipients tab — third left-pane tab in the email builder.
 *
 * Mounts three sections, top-to-bottom:
 *
 *  1. **Verteiler** — multi-select dropdown of the caller's customer-kind
 *     contact lists. Picking one materialises its members as recipients on
 *     the merged list below; removing it does not unpick the ad-hoc rows
 *     that happen to overlap (dedupe is by email).
 *  2. **Ad-hoc** — `ContactEmployeePicker` in multi-select mode.
 *  3. **Merged recipient list** — one row per unique email, marked with
 *     where it came from (V / A / both). The send action sits below.
 *  4. **Recent sends** — last few send-log entries from the backend.
 *
 * Send/Test-send results are surfaced via notistack toasts plus a inline
 * status strip with the per-status counts (sent / blocked / failed).
 */
export function RecipientsPane({
  draft,
  contactListIds,
  onContactListIdsChange,
  adHocContactIds,
  onAdHocContactIdsChange,
  subject,
  onSubjectChange,
}: RecipientsPaneProps) {
  const { data: lists = [], isLoading: listsLoading } = useContactLists();
  const { data: contacts = [] } = useGetAllContactEmployees();
  const { data: consentIds } = useRecipientConsent();
  const { data: recentSends = [] } = useEmailBuilderSends();
  const { mutate: sendDraft, isPending: isSending } = useSendEmailDraft();
  const { mutate: testSend, isPending: isTesting } = useTestSendEmailDraft();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * SUP-40 — restrict the send to contacts that consented to mail reception
   * (an enabled stocklist subscription). Local to the pane: the pane stays
   * mounted across tab switches, so this survives without lifting to the page.
   */
  const [onlyConsenting, setOnlyConsenting] = useState(false);
  const consentSet = useMemo(
    () => consentIds ?? new Set<number>(),
    [consentIds],
  );

  // Customer-kind only — supplier Verteiler aren't a typical email-builder
  // audience (those flow through sourcing). Same constraint the picker
  // surfaces server-side via the kind invariant.
  const customerLists = useMemo(
    () => lists.filter((l) => l.kind === 'customer'),
    [lists],
  );

  const contactsById = useMemo(() => {
    const map = new Map<number, ContactEmployee>();
    for (const c of contacts) map.set(c.id, c);
    return map;
  }, [contacts]);

  /**
   * Merged, deduped recipient projection. Verteiler-derived rows come first
   * (preserving the order the user picked the lists); ad-hoc picks follow.
   * Each row carries a tag with its origin so the user can spot duplicates.
   */
  const recipientRows = useMemo(() => {
    type Row = {
      key: string;
      contactEmployeeId: number;
      name: string;
      email: string;
      businessPartnerName: string | null;
      origin: 'verteiler' | 'adhoc' | 'both';
      missingEmail: boolean;
      hasConsent: boolean;
    };
    const rows = new Map<string, Row>();

    const addRow = (
      contact: ContactEmployee,
      origin: 'verteiler' | 'adhoc',
    ) => {
      const emailRaw = (contact.email ?? '').trim();
      const key = emailRaw ? emailRaw.toLowerCase() : `__no-email-${contact.id}`;
      const existing = rows.get(key);
      if (existing) {
        if (existing.origin !== origin) existing.origin = 'both';
        return;
      }
      rows.set(key, {
        key,
        contactEmployeeId: contact.id,
        name: contact.name,
        email: emailRaw,
        businessPartnerName: contact.businessPartner?.name ?? null,
        origin,
        missingEmail: emailRaw.length === 0,
        hasConsent: consentSet.has(contact.id),
      });
    };

    // Verteiler members aren't always present in the all-contacts list
    // (Verteiler can pick from any BP), so we have to walk the list's
    // own materialised members, not the contacts map.
    for (const listId of contactListIds) {
      const list = lists.find((l) => l.id === listId);
      if (!list) continue;
      for (const m of list.members) {
        const c = contactsById.get(m.contactEmployeeId);
        if (c) {
          addRow(c, 'verteiler');
        } else {
          // Member exists on the list but isn't in the contacts cache —
          // build a minimal shape from the list-member fields so it still
          // shows up in the recipient list.
          addRow(
            {
              id: m.contactEmployeeId,
              name: m.name,
              firstName: '',
              middleName: null,
              lastName: '',
              position: m.position,
              phone1: null,
              phone2: null,
              email: m.email,
              remarks1: null,
              remarks2: null,
              businessPartner: {
                id: m.businessPartnerId,
                name: m.businessPartnerName,
                type: m.businessPartnerType,
              } as ContactEmployee['businessPartner'],
            },
            'verteiler',
          );
        }
      }
    }
    for (const id of adHocContactIds) {
      const c = contactsById.get(id);
      if (c) addRow(c, 'adhoc');
    }
    return Array.from(rows.values());
  }, [contactListIds, adHocContactIds, lists, contactsById, consentSet]);

  // Consenting rows that are actually deliverable — the count the filter and
  // the send both key on.
  const consentingCount = recipientRows.filter(
    (r) => r.hasConsent && !r.missingEmail,
  ).length;

  // When the consent filter is on, only consenting rows are shown and sent.
  const visibleRows = useMemo(
    () => (onlyConsenting ? recipientRows.filter((r) => r.hasConsent) : recipientRows),
    [recipientRows, onlyConsenting],
  );

  const deliverableCount = visibleRows.filter((r) => !r.missingEmail).length;
  const noEmailCount = visibleRows.length - deliverableCount;

  const toggleList = useCallback(
    (id: number) => {
      onContactListIdsChange(
        contactListIds.includes(id)
          ? contactListIds.filter((x) => x !== id)
          : [...contactListIds, id],
      );
    },
    [contactListIds, onContactListIdsChange],
  );

  const addAdHoc = useCallback(
    (id: number) => {
      if (!adHocContactIds.includes(id)) {
        onAdHocContactIdsChange([...adHocContactIds, id]);
      }
    },
    [adHocContactIds, onAdHocContactIdsChange],
  );

  /**
   * Removes a recipient by key. Verteiler-derived rows can't be removed in
   * isolation (you'd have to unpick the whole Verteiler) — we surface that
   * by disabling the button on those rows. Ad-hoc removes drop the id; rows
   * tagged 'both' fall back to 'verteiler' once the ad-hoc id is gone.
   */
  const removeAdHoc = useCallback(
    (contactEmployeeId: number) => {
      onAdHocContactIdsChange(
        adHocContactIds.filter((x) => x !== contactEmployeeId),
      );
    },
    [adHocContactIds, onAdHocContactIdsChange],
  );

  const canSend = subject.trim().length > 0 && deliverableCount > 0 && !isSending;
  const canTest = !isTesting;

  const handleSend = useCallback(() => {
    const payload: SendEmailRequest = {
      subject: subject.trim(),
      preheader: draft.preheader,
      contactPerson: draft.contactPerson,
      blocks: draft.blocks,
      recipients: { contactListIds, contactEmployeeIds: adHocContactIds },
      onlyConsenting,
    };
    sendDraft(payload, {
      onSuccess: (res) => {
        const parts: string[] = [];
        if (res.sentCount > 0) parts.push(`${res.sentCount} versendet`);
        if (res.skippedCount > 0)
          parts.push(`${res.skippedCount} blockiert (Test-Umgebung)`);
        if (res.failedCount > 0) parts.push(`${res.failedCount} fehlgeschlagen`);
        enqueueSnackbar(parts.join(' · ') || 'Nichts zu versenden.', {
          variant: res.failedCount > 0 ? 'warning' : 'success',
        });
        // Reset the recipient picks but keep the draft. Avoids accidental
        // double-sends to the same audience on a follow-up tweak.
        onContactListIdsChange([]);
        onAdHocContactIdsChange([]);
      },
      onError: (err) =>
        enqueueSnackbar(
          (err.response?.data as { message?: string })?.message ||
            'Senden fehlgeschlagen.',
          { variant: 'error' },
        ),
    });
  }, [
    subject,
    draft,
    contactListIds,
    adHocContactIds,
    onlyConsenting,
    sendDraft,
    enqueueSnackbar,
    onContactListIdsChange,
    onAdHocContactIdsChange,
  ]);

  const handleTestSend = useCallback(() => {
    testSend(
      {
        subject: subject.trim() || undefined,
        preheader: draft.preheader,
        contactPerson: draft.contactPerson,
        blocks: draft.blocks,
        previewRecipient: draft.previewRecipient,
      },
      {
        onSuccess: (res) => {
          const sent = res.sentCount > 0;
          enqueueSnackbar(
            sent
              ? 'Test-E-Mail an dich versendet.'
              : res.skippedCount > 0
                ? 'Test blockiert (Test-Umgebung).'
                : 'Test-Versand fehlgeschlagen.',
            { variant: sent ? 'success' : 'warning' },
          );
        },
        onError: (err) =>
          enqueueSnackbar(
            (err.response?.data as { message?: string })?.message ||
              'Test-Versand fehlgeschlagen.',
            { variant: 'error' },
          ),
      },
    );
  }, [subject, draft, testSend, enqueueSnackbar]);

  return (
    <div className="eb-recipients">
      <div className="eb-recipients-section">
        <div className="eb-recipients-section-head">
          <span className="eb-recipients-section-title">Betreff</span>
        </div>
        <DsInput
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder="Pflichtfeld – wird im Posteingang angezeigt"
        />
      </div>

      <div className="eb-recipients-section">
        <div className="eb-recipients-section-head">
          <span className="eb-recipients-section-title">Verteiler</span>
          {customerLists.length > 0 && (
            <span className="eb-recipients-section-hint">
              {customerLists.length} verfügbar · nur Kunden-Verteiler
            </span>
          )}
        </div>
        {listsLoading ? (
          <div className="eb-recipients-empty">Lade Verteiler…</div>
        ) : customerLists.length === 0 ? (
          <div className="eb-recipients-empty">
            Noch keine Kunden-Verteiler. Lege einen unter{' '}
            <em>Geschäftspartner → Verteiler</em> an.
          </div>
        ) : (
          <ul className="eb-vt-list" role="list">
            {customerLists.map((list) => {
              const picked = contactListIds.includes(list.id);
              return (
                <li key={list.id}>
                  <button
                    type="button"
                    className={`eb-vt-chip${picked ? ' is-picked' : ''}`}
                    aria-pressed={picked}
                    onClick={() => toggleList(list.id)}
                  >
                    <span className="eb-vt-chip-name">{list.name}</span>
                    <span className="eb-vt-chip-count">
                      {list.members.length}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="eb-recipients-section">
        <div className="eb-recipients-section-head">
          <span className="eb-recipients-section-title">
            Einzelne Kontakte hinzufügen
          </span>
        </div>
        <ContactEmployeePicker
          mode="multi"
          selectedIds={adHocContactIds}
          onAdd={addAdHoc}
          businessPartnerTypeFilter={['cCustomer']}
          placeholder="Kontakt suchen und hinzufügen…"
        />
      </div>

      <div className="eb-recipients-section eb-recipients-list-section">
        <div className="eb-recipients-section-head">
          <span className="eb-recipients-section-title">
            Empfänger ({deliverableCount}
            {noEmailCount > 0 && ` · ${noEmailCount} ohne E-Mail`})
          </span>
          {recipientRows.length > 0 && (
            <label
              className={`eb-consent-toggle${onlyConsenting ? ' is-on' : ''}`}
              title="Nur Kontakte versenden, die dem Mailempfang (z. B. der Bestandsliste) zugestimmt haben"
            >
              <input
                type="checkbox"
                checked={onlyConsenting}
                onChange={(e) => setOnlyConsenting(e.target.checked)}
              />
              <span>Nur mit Mailempfang ({consentingCount})</span>
            </label>
          )}
        </div>
        {recipientRows.length === 0 ? (
          <div className="eb-recipients-empty">
            Noch keine Empfänger. Wähle einen Verteiler oder einen Kontakt aus.
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="eb-recipients-empty">
            Keiner der gewählten Kontakte hat dem Mailempfang zugestimmt.
          </div>
        ) : (
          <ul className="eb-recipient-rows" role="list">
            {visibleRows.map((row) => (
              <li
                key={row.key}
                className={`eb-recipient-row${row.missingEmail ? ' is-missing' : ''}`}
              >
                <span className="eb-recipient-origin" data-origin={row.origin}>
                  {row.origin === 'verteiler'
                    ? 'V'
                    : row.origin === 'adhoc'
                      ? 'A'
                      : 'V+A'}
                </span>
                <span
                  className={`eb-recipient-consent${row.hasConsent ? ' is-on' : ''}`}
                  title={
                    row.hasConsent
                      ? 'Hat dem Mailempfang zugestimmt'
                      : 'Keine Mailempfang-Einwilligung'
                  }
                  aria-label={
                    row.hasConsent
                      ? 'Mailempfang erlaubt'
                      : 'Kein Mailempfang'
                  }
                >
                  {row.hasConsent ? '✓' : '–'}
                </span>
                <span className="eb-recipient-name">{row.name}</span>
                {row.businessPartnerName && (
                  <span className="eb-recipient-bp">
                    · {row.businessPartnerName}
                  </span>
                )}
                <span className="eb-recipient-email">
                  {row.email || (
                    <em className="eb-recipient-no-email">keine E-Mail</em>
                  )}
                </span>
                {(row.origin === 'adhoc' || row.origin === 'both') && (
                  <button
                    type="button"
                    className="eb-recipient-remove"
                    aria-label="Aus Ad-hoc entfernen"
                    onClick={() => removeAdHoc(row.contactEmployeeId)}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <IfPermitted
        permission={PERMISSIONS.EMAIL_BUILDER_SEND}
        fallback={
          <div className="eb-recipients-actions">
            <span
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--ink-3)',
              }}
            >
              Zum Versenden fehlt die Berechtigung „E-Mails versenden".
            </span>
          </div>
        }
      >
        <div className="eb-recipients-actions">
          <DsButton
            variant="ghost"
            onClick={handleTestSend}
            disabled={!canTest}
            title="Sendet eine einzelne Kopie an deine eigene E-Mail-Adresse"
          >
            {isTesting ? 'Sende Test…' : 'Test an mich'}
          </DsButton>
          <DsButton
            variant="primary"
            onClick={handleSend}
            disabled={!canSend}
            title={
              subject.trim().length === 0
                ? 'Betreff fehlt'
                : deliverableCount === 0
                  ? 'Keine Empfänger mit E-Mail-Adresse'
                  : 'Jetzt versenden'
            }
          >
            {isSending
              ? 'Sende…'
              : `Jetzt versenden${deliverableCount > 0 ? ` (${deliverableCount})` : ''}`}
          </DsButton>
        </div>
      </IfPermitted>

      {recentSends.length > 0 && (
        <div className="eb-recipients-section eb-recipients-recent">
          <div className="eb-recipients-section-head">
            <span className="eb-recipients-section-title">Letzte Versendungen</span>
          </div>
          <ul className="eb-recent-sends" role="list">
            {recentSends.slice(0, 6).map((send) => (
              <RecentSendRow key={send.id} send={send} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RecentSendRow({ send }: { send: EmailBuilderSendSummary }) {
  const when = formatRelative(send.createdAt);
  const tone =
    send.failedCount > 0 ? 'failed' : send.skippedCount > 0 ? 'partial' : 'ok';
  return (
    <li className="eb-recent-send" data-tone={tone}>
      <span className="eb-recent-when">{when}</span>
      <span className="eb-recent-subject">
        {send.isTest && (
          <span className="eb-recent-test-flag" title="Test-Versand">
            T
          </span>
        )}
        {send.subject}
      </span>
      <span className="eb-recent-counts">
        <strong>{send.sentCount}</strong>
        {send.skippedCount > 0 && (
          <span title="Blockiert (nicht-prod Umgebung)">
            · {send.skippedCount} blockiert
          </span>
        )}
        {send.failedCount > 0 && (
          <span title="Fehlgeschlagen">· {send.failedCount} Fehler</span>
        )}
      </span>
    </li>
  );
}

/** Compact relative time-ago. The recent-sends footer is glanceable; full
 *  timestamps would dominate the row. */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} ${d === 1 ? 'Tag' : 'Tagen'}`;
  const date = new Date(iso);
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.`;
}
